import {
  Component,
  ChangeDetectorRef,
  HostBinding,
  Input,
  OnChanges,
  OnDestroy,
  SimpleChanges,
} from '@angular/core';
import { Subscription } from 'rxjs';

import {
  ReportOutputKind,
  ReportQuery,
  ReportQueryService,
  ReportVisualization,
  ReportWidgetData,
} from '../../../services/report-query';
import { DashboardWidgetLayout } from '../dashboard-grid/dashboard-grid';
import { dashboardErrorMessage } from '../../widget-and-graphs.models';

export type ReportWidgetIcon = 'money' | 'orders' | 'average' | 'percentage' | 'sales' | 'staff';

@Component({
  selector: 'app-report-widget',
  standalone: false,
  templateUrl: './report-widget.html',
  styleUrl: './report-widget.scss',
})
export class ReportWidget implements OnChanges, OnDestroy {
  @Input() iconSvg = '';
  @Input({ required: true }) query!: ReportQuery;
  @Input({ required: true }) output!: ReportOutputKind;
  @Input({ required: true }) title = '';
  @Input() subtitle = '';
  @Input() visualization: ReportVisualization = 'number';
  @Input() icon: ReportWidgetIcon = 'sales';
  @Input() layout: DashboardWidgetLayout = { xs: { width: 12, height: 3 } };
  @Input() colors: string[] = ['#206bc4', '#2fb344', '#f59f00', '#d63939', '#ae3ec9'];
  @Input() currencyCode = 'USD';
  @Input() locale = 'en-US';
  @Input() emptyText = 'No data for this period.';
  @Input() showTimeControls = true;
  @Input() durationOptions: number[] = [7, 14, 30, 90, 180, 366];
  @Input() from?: string;
  @Input() duration?: number;

  loading = false;
  error = '';
  data?: ReportWidgetData;
  selectedFrom = '';
  selectedDuration = 30;
  private request?: Subscription;

  constructor(
    private readonly reportQuery: ReportQueryService,
    private readonly cdr: ChangeDetectorRef,
  ) {}

  @HostBinding('style.--widget-accent')
  get accent(): string {
    return this.colors[0] ?? '#206bc4';
  }

  @HostBinding('style.--widget-xs-w') get xsWidth(): string { return `${this.size('xs').width}`; }
  @HostBinding('style.--widget-xs-h') get xsHeight(): string { return `${this.size('xs').height}`; }
  @HostBinding('style.--widget-sm-w') get smWidth(): string { return `${this.size('sm').width}`; }
  @HostBinding('style.--widget-sm-h') get smHeight(): string { return `${this.size('sm').height}`; }
  @HostBinding('style.--widget-md-w') get mdWidth(): string { return `${this.size('md').width}`; }
  @HostBinding('style.--widget-md-h') get mdHeight(): string { return `${this.size('md').height}`; }
  @HostBinding('style.--widget-lg-w') get lgWidth(): string { return `${this.size('lg').width}`; }
  @HostBinding('style.--widget-lg-h') get lgHeight(): string { return `${this.size('lg').height}`; }
  @HostBinding('style.--widget-xl-w') get xlWidth(): string { return `${this.size('xl').width}`; }
  @HostBinding('style.--widget-xl-h') get xlHeight(): string { return `${this.size('xl').height}`; }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['query'] || changes['from'] || changes['duration'] || changes['output']) {
      this.selectedFrom = this.toDateInput(this.from ?? this.query?.from ?? '');
      this.selectedDuration = this.duration ?? this.query?.duration ?? 30;
      this.load();
    }
  }

  ngOnDestroy(): void {
    this.request?.unsubscribe();
  }

  reloadForTimeWindow(): void {
    this.load();
  }

  get hasRows(): boolean {
    return (this.data?.result.rows.length ?? 0) > 0;
  }

  get scalarValue(): unknown {
    const key = this.data?.verification.bindings.value;
    return key ? this.data?.result.rows[0]?.[key] ?? 0 : 0;
  }

  get scalarType() {
    const key = this.data?.verification.bindings.value;
    return this.data?.result.columns.find((column) => column.key === key)?.data_type ?? 'number';
  }

  formatTableValue(value: unknown, dataType: string): string {
    if (value === null || value === undefined || value === '') return '—';
    if (dataType === 'currency') return new Intl.NumberFormat(this.locale, { style: 'currency', currency: this.currencyCode, maximumFractionDigits: 2 }).format(Number(value));
    if (dataType === 'number' || dataType === 'integer') return new Intl.NumberFormat(this.locale, { maximumFractionDigits: 2 }).format(Number(value));
    if (dataType === 'percentage') return `${new Intl.NumberFormat(this.locale, { maximumFractionDigits: 1 }).format(Number(value))}%`;
    if (dataType === 'date' || dataType === 'datetime') { const parsed = new Date(String(value)); return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleDateString(this.locale); }
    return String(value);
  }

  load(): void {
    this.request?.unsubscribe();
    this.error = '';
    this.data = undefined;

    if (!this.query?.from || !this.query?.duration) {
      this.error = 'This widget query must include both from and duration.';
      return;
    }
    if (!this.selectedFrom || this.selectedDuration < 1 || this.selectedDuration > 366) {
      this.error = 'Choose a start date and a duration from 1 to 366 days.';
      return;
    }

    const from = new Date(`${this.selectedFrom}T00:00:00.000Z`).toISOString();
    const boundedQuery = this.reportQuery.withTimeWindow(this.query, from, this.selectedDuration);
    this.loading = true;
    this.request = this.reportQuery.loadWidget(boundedQuery, this.output).subscribe({
      next: (data) => {
        this.data = data;
        this.loading = false;
        this.cdr.detectChanges();
      },
      error: (error: unknown) => {
        this.error = dashboardErrorMessage(error);
        this.loading = false;
        this.cdr.detectChanges();
      },
    });
  }

  private size(breakpoint: keyof DashboardWidgetLayout) {
    const order: Array<keyof DashboardWidgetLayout> = ['xs', 'sm', 'md', 'lg', 'xl'];
    const index = order.indexOf(breakpoint);
    for (let cursor = index; cursor >= 0; cursor -= 1) {
      const size = this.layout[order[cursor]];
      if (size) {
        return {
          width: Math.max(1, Math.min(12, size.width)),
          height: Math.max(1, size.height),
        };
      }
    }
    return { width: 12, height: 3 };
  }

  private toDateInput(value: string): string {
    if (!value) return '';
    const parsed = new Date(value);
    return Number.isNaN(parsed.getTime()) ? '' : parsed.toISOString().slice(0, 10);
  }

}
